import { useState, useEffect, useRef } from 'react';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';

interface AlbumDownloadState {
  status: 'idle' | 'preparing' | 'ready';
  downloadUrl: string | null;
  expiresAt: string | null;
  exportId: string | null;
}


export const useAlbumDownload = () => {
  const { albumId } = useAlbumContext();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [state, setState] = useState<AlbumDownloadState>({
    status: 'idle',
    downloadUrl: null,
    expiresAt: null,
    exportId: null,
  });
  const pollingInterval = useRef<number | null>(null);

  // Fetch latest export from Supabase
  const fetchLatestExport = async () => {
    try {
      const { data, error } = await supabase
        .from('exports')
        .select('*')
        .eq('album_id', albumId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching export:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in fetchLatestExport:', error);
      return null;
    }
  };

  // Check and update state based on latest export
  const updateStateFromExport = async () => {
    const exportData = await fetchLatestExport();

    if (!exportData) {
      return;
    }

    // Treat very old "processing" exports as stale to avoid stuck UI
    const createdAt = exportData.created_at ? new Date(exportData.created_at) : null;
    const ageMs = createdAt ? Date.now() - createdAt.getTime() : 0;
    const STALE_MS = 15 * 60 * 1000; // 15 minutes

    // Check if export is still processing
    if (exportData.is_processing && ageMs <= STALE_MS) {
      setState({
        status: 'preparing',
        downloadUrl: null,
        expiresAt: null,
        exportId: exportData.id,
      });

      // Start polling if not already polling
      if (!pollingInterval.current) {
        pollingInterval.current = window.setInterval(async () => {
          const latestExport = await fetchLatestExport();
          if (latestExport) {
            const latestCreatedAt = latestExport.created_at ? new Date(latestExport.created_at) : null;
            const latestAgeMs = latestCreatedAt ? Date.now() - latestCreatedAt.getTime() : 0;
            const stillProcessing = latestExport.is_processing && latestAgeMs <= STALE_MS;
            if (!stillProcessing) {
              // Processing complete or stale, update state
              if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
                pollingInterval.current = null;
              }
              await updateStateFromExport();
            }
          }
        }, 2000);
      }
      return;
    }

    // If processing is stale or not processing, continue with ready/idle handling

    // Export is complete, check if expired
    if (exportData.download_url) {
      const expiresAt = exportData.expires_at ? new Date(exportData.expires_at) : null;
      const isExpired = expiresAt && expiresAt < new Date();

      if (isExpired) {
        setState({
          status: 'idle',
          downloadUrl: null,
          expiresAt: null,
          exportId: null,
        });
      } else {
        setState({
          status: 'ready',
          downloadUrl: exportData.download_url,
          expiresAt: exportData.expires_at,
          exportId: exportData.id,
        });
      }
    } else {
      setState({
        status: 'idle',
        downloadUrl: null,
        expiresAt: null,
        exportId: null,
      });
    }
  };

  // Load state on mount
  useEffect(() => {
    updateStateFromExport();

    // Cleanup polling on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [albumId]);

  const startDownload = async () => {
    try {
      setState({ status: 'preparing', downloadUrl: null, expiresAt: null, exportId: null });

      // Insert a new export record in Supabase
      const { data: newExport, error: insertError } = await supabase
        .from('exports')
        .insert({
          album_id: albumId,
          is_processing: true,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating export record:', insertError);
        throw new Error('Failed to create export record');
      }

      const response = await fetch(
        `https://floren8n.fly.dev/webhook/bloomkeep-download-album?albumId=${albumId}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Album download failed:', errorText);
        throw new Error('Failed to prepare album download');
      }

      // Start polling to check for completion
      await updateStateFromExport();

      toast({
        title: t('success'),
        description: t('albumDownloadReady'),
      });
    } catch (error) {
      console.error('Error preparing album download:', error);
      setState({ status: 'idle', downloadUrl: null, expiresAt: null, exportId: null });

      toast({
        title: t('error'),
        description: t('couldNotPrepareAlbumDownload'),
        variant: 'destructive',
      });
    }
  };

  const triggerDownload = async () => {
    if (state.downloadUrl && state.exportId) {
      // Increment downloaded_times
      try {
        const { data: currentExport } = await supabase
          .from('exports')
          .select('downloaded_times')
          .eq('id', state.exportId)
          .single();

        if (currentExport) {
          await supabase
            .from('exports')
            .update({ downloaded_times: currentExport.downloaded_times + 1 })
            .eq('id', state.exportId);
        }
      } catch (error) {
        console.error('Error in triggerDownload:', error);
      }

      window.open(state.downloadUrl, '_blank');
    }
  };

  const reset = () => {
    setState({ status: 'idle', downloadUrl: null, expiresAt: null, exportId: null });
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  return {
    state,
    startDownload,
    triggerDownload,
    reset,
  };
};
